import React, { useState, useRef, useEffect } from "react";
import DemoImg from "../../assets/logo.png";
import axios from 'axios';
import { LuArrowRight, LuCheck, LuStar } from "react-icons/lu";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const Demo = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    nom: "",
    email: "",
    societe: "",
    telephone: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  // Turnstile : activé uniquement si une vraie sitekey est fournie via l'env.
  // Sans clé configurée, aucun token n'est envoyé — même chemin fiable que le
  // formulaire de contact (le backend ne vérifie Turnstile que si un token est présent).
  const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    
    const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://127.0.0.1:5003';
    const country = localStorage.getItem('yros_country') || 'sn';

    try {
      const { data } = await axios.post(`${API_URL}/api/contact`, {
        country,
        name: form.nom,
        email: form.email,
        company: form.societe,
        phone: form.telephone,
        message: form.message,
        subject: `Demande de démo — ${form.nom}`,
        turnstile_token: turnstileToken || undefined,
      });

      if (data?.status !== 'sent') {
        throw new Error(data?.error || 'Envoi échoué');
      }

      setSent(true);
      setForm({
        nom: "",
        email: "",
        societe: "",
        telephone: "",
        message: "",
      });
    } catch (err) {
      console.error('Erreur d\'envoi:', err);
      setError(t('demo.error_generic'));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!TURNSTILE_SITEKEY) return; // captcha désactivé tant qu'aucune sitekey n'est configurée
    let mounted = true;
    const renderWidget = () => {
      if (!mounted) return;
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId) {
        try {
          const id = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITEKEY,
            callback: (token) => {
              setTurnstileToken(token);
            },
            'error-callback': (code) => {
              // Affiche le vrai code Cloudflare (ex: 110200 = domaine non autorisé)
              console.error('Turnstile error (demo), code =', code);
              return true; // évite que Turnstile relance une erreur opaque « Script error. »
            },
            'expired-callback': () => setTurnstileToken('')
          });
          setTurnstileWidgetId(id);
        } catch (err) {
          console.warn('Turnstile render failed (demo):', err);
        }
      } else if (!window.turnstile) {
        setTimeout(renderWidget, 300);
      }
    };

    renderWidget();
    return () => { mounted = false; };
  }, [TURNSTILE_SITEKEY, turnstileRef, turnstileWidgetId]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const benefits = t('demo.benefits', { returnObjects: true });

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20 px-4">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-4 shadow-lg">
            <LuStar className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-gray-700">{t('demo.badge')}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('demo.heading')}{" "}
            <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
              {t('demo.heading_highlight')}
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('demo.subtitle')}
          </p>
        </motion.div>

        <div className="bg-white rounded-[2rem] shadow-xl max-w-5xl mx-auto overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* Colonne image/bénéfices */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="md:w-2/5 bg-gradient-to-br from-orange-50 to-yellow-50 p-10 flex flex-col"
            >
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-300/30 to-yellow-300/30 rounded-full blur-lg transform scale-125"></div>
                  <img
                    src={DemoImg}
                    alt={t('demo.image_alt')}
                    className="w-32 h-32 object-contain relative"
                    loading="lazy"
                  />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('demo.discover_heading')}
              </h2>
              
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-4 flex-grow"
              >
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    variants={itemVariants}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-center flex-shrink-0">
                      <LuCheck className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700">{benefit}</p>
                  </motion.div>
                ))}
              </motion.div>
              
              <div className="mt-8 pt-8 border-t border-orange-200/50">
                <p className="text-gray-600 italic">
                  "{t('demo.quote')}"
                </p>
              </div>
            </motion.div>
            
            {/* Colonne formulaire */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="md:w-3/5 p-10"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('demo.form_heading')}
              </h3>

              {sent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200 text-center space-y-4"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                    <LuCheck className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">{t('demo.sent_title')}</h4>
                  <p className="text-gray-600">
                    {t('demo.sent_message')}
                  </p>
                  <button
                    onClick={() => {setSent(false)}}
                    className="inline-flex items-center gap-2 text-green-600 font-medium hover:text-green-700 transition-colors mt-4"
                  >
                    {t('demo.send_another')}
                    <LuArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-5" 
                  onSubmit={handleSubmit}
                >
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 text-red-600 p-4 rounded-xl text-center border border-red-200"
                    >
                      {error}
                    </motion.div>
                  )}
                  
                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="nom">
                      {t('demo.label_name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      required
                      value={form.nom}
                      onChange={handleChange}
                      placeholder={t('demo.placeholder_name')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="email">
                      {t('demo.label_email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('demo.placeholder_email')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <motion.div variants={itemVariants}>
                      <label className="block text-sm font-medium mb-2" htmlFor="societe">
                        {t('demo.label_company')}
                      </label>
                      <input
                        type="text"
                        id="societe"
                        name="societe"
                        value={form.societe}
                        onChange={handleChange}
                        placeholder={t('demo.placeholder_company')}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <label className="block text-sm font-medium mb-2" htmlFor="telephone">
                        {t('demo.label_phone')}
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={form.telephone}
                        onChange={handleChange}
                        placeholder={t('demo.placeholder_phone')}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                      />
                    </motion.div>
                  </div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="message">
                      {t('demo.label_message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t('demo.placeholder_message')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>
                  
                  {TURNSTILE_SITEKEY && (
                    <div className="mb-4">
                      <div ref={turnstileRef} id="turnstile-demo" />
                    </div>
                  )}

                  <motion.button
                    variants={itemVariants}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={sending}
                    className={`w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                      sending ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {sending ? t('demo.sending') : t('demo.submit')}
                    {!sending && <LuArrowRight className="w-5 h-5" />}
                  </motion.button>

                  <motion.p variants={itemVariants} className="text-sm text-gray-500 text-center mt-4">
                    {t('demo.consent_note')}
                  </motion.p>
                </motion.form>
              )}
            </motion.div>
          </div>
        </div>
        
        {/* Additional info */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-gray-600">
            {t('demo.questions_note')} <a href="/contact" className="text-orange-500 font-medium hover:text-orange-600 transition-colors">{t('demo.contact_us_link')}</a>
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default Demo;